import React from 'react';
import Divider from 'flow/components/ui-components/Divider';
import RadioButton from 'flow/components/ui-components/RadioButton';
import useAppContext from 'flow/hooks/useAppContext';
import useFeatureFlags from 'flow/hooks/useFeatureFlags';
import { CallRecordingSettings, SettingsType } from 'flow/types/CallRecording';
import { Profile } from 'flow/types/Profile';
import { TypedMap } from 'flow/types/TypedMap';
import {
    getCallRecordingValue,
    keysMappedBySettingsType,
} from 'flow/utils/call-recording-monitoring-utils';

interface Props {
    profile: TypedMap<Profile>;
    onSelected: (value: CallRecordingSettings) => void;
    settingsType: SettingsType;
    storingOption: CallRecordingSettings;
}

const ProfileCallRecordingSelection = ({
    profile,
    onSelected,
    settingsType,
    storingOption,
}: Props) => {
    const { i18next } = useAppContext();
    const { hasFeature } = useFeatureFlags();

    const getAvailableOptions = () => {
        const { recordCalls, transcribe } =
            keysMappedBySettingsType[settingsType];
        const options: CallRecordingSettings[] = [CallRecordingSettings.Off];

        if (profile.has(recordCalls)) {
            options.push(CallRecordingSettings.RecordCalls);
        }

        if (profile.has(recordCalls) && profile.has(transcribe)) {
            options.push(CallRecordingSettings.RecordAndAutoTranscribe);
        }

        if (
            hasFeature('CAN_AUTO_TRANSCRIBE_WITHOUT_RECORDED_CALLS') &&
            profile.has(transcribe)
        ) {
            options.push(CallRecordingSettings.AutoTranscribeOnly);
        }

        return options;
    };

    const selectedOption = getCallRecordingValue(
        profile,
        settingsType,
        hasFeature('CAN_AUTO_TRANSCRIBE_WITHOUT_RECORDED_CALLS'),
    );

    return (
        <>
            {getAvailableOptions().map((option) => (
                <RadioButton
                    key={`callRecording-${option}`}
                    name="callRecordingOptions"
                    checked={option === selectedOption}
                    onChange={() => onSelected(option)}
                    disabled={!!storingOption}
                    showLoadingSpinner={option === storingOption}
                >
                    {i18next.t(`settings.call_recording.${option}`)}
                </RadioButton>
            ))}

            <Divider />

            <span className="px-2 text-sm text-gray-500">
                {selectedOption === CallRecordingSettings.AutoTranscribeOnly &&
                    i18next.t(
                        'settings.call_recording.autoTranscribeOnly.description',
                    )}
            </span>
        </>
    );
};

export default ProfileCallRecordingSelection;
